import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Badge, Button, CellText, DotsVertical, Drawer, Menu, ProgressBar, Save01, Table } from '@opendate-app/setlist';
import SeatReassignmentModal from '../../OrderPage/components/SeatReassignmentModal';
import SeatRefundDialog from './SeatRefundDialog';
import useOrderSeatReassignment from '../../OrderPage/hooks/useOrderSeatReassignment';
import { toastError, toastSuccess } from '../../../shared/toastHelper';

const money = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount) || 0);
const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

const count = (items, one, many) => (items || []).length ? `${items.length} ${items.length === 1 ? one : many}` : null;

export const alsoTakenSummary = (report) => [
  count(report.held, 'held seat', 'held seats'),
  count(report.killed, 'killed seat', 'killed seats'),
  count(report.carts, 'seat in a cart', 'seats in a cart'),
  count(report.shrunk, 'area too small for what sold', 'areas too small for what sold'),
  count(report.taken, 'other taken seat', 'other taken seats'),
  count(report.releasing, 'seat freeing up', 'seats freeing up'),
].filter(Boolean).join(' and ');

export const removedSummary = (labels) => {
  const rows = {};
  const others = [];
  labels.forEach((label) => {
    const match = label.match(/^(.+)-(\d+)$/);
    if (match) (rows[match[1]] = rows[match[1]] || []).push(Number(match[2]));
    else others.push(label);
  });
  const parts = Object.entries(rows).map(([row, seats]) => {
    const sortedSeats = seats.sort((a, b) => a - b);
    const contiguous = sortedSeats.every((seat, i) => i === 0 || seat === sortedSeats[i - 1] + 1);
    if (sortedSeats.length === 1) return `Row ${row} seat ${sortedSeats[0]}`;
    return contiguous ? `Row ${row} seats ${sortedSeats[0]}\u2013${sortedSeats[sortedSeats.length - 1]}` : `Row ${row} seats ${sortedSeats.join(', ')}`;
  });
  return [...parts, ...others].join(', ');
};

const sectionTitleStyle = { fontSize: 16, lineHeight: '24px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 };
const countStyle = { fontSize: 12, lineHeight: '18px', fontWeight: 600, color: '#414651', background: '#FAFAFA', border: '1px solid #E9EAEB', borderRadius: 6, padding: '0 6px' };
const fieldLabelStyle = { fontSize: 14, lineHeight: '20px', fontWeight: 600, color: '#414651', marginBottom: 8 };
const miniRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '12px 16px', border: '1px solid #E9EAEB', borderRadius: 12, background: '#fff', marginTop: 8 };
const hint = { fontSize: 14, lineHeight: '20px', color: '#535862' };

const statusBadge = (row) => {
  if (row.status === 'moved') return <Badge size="sm" color="success">Moved to {row.to}</Badge>;
  if (row.status === 'refunded') return <Badge size="sm" color="gray">Refunded</Badge>;
  if (row.status === 'sorted') return <Badge size="sm" color="gray">Refunded or moved</Badge>;
  return <Badge size="sm" color="warning" dot>Needs a seat</Badge>;
};

const SeatsInTheWayPanel = ({ open, onClose, report, onRefresh, onSave, saveLabel }) => {
  const [moved, setMoved] = useState({});
  const [releasing, setReleasing] = useState(null);
  const [releasedHolds, setReleasedHolds] = useState([]);
  const firstSold = useRef({});
  const released = useRef(new Set());
  const orderOpened = useRef(false);
  const mostInTheWay = useRef(0);
  const [active, setActive] = useState(null);
  const pickedSeat = useRef(null);
  const [selected, setSelected] = useState([]);
  const [movingMany, setMovingMany] = useState(null);
  const [suggested, setSuggested] = useState(null);
  const [submittingMany, setSubmittingMany] = useState(false);
  const [refunding, setRefunding] = useState(null);
  const [submittingRefund, setSubmittingRefund] = useState(false);
  const [refunded, setRefunded] = useState({});

  useEffect(() => {
    (report?.sold || []).forEach((row) => { if (!firstSold.current[row.id]) firstSold.current[row.id] = row; });
  }, [report]);

  useEffect(() => {
    const refreshWhenBack = () => {
      if (document.visibilityState !== 'visible' || !orderOpened.current) return;
      orderOpened.current = false;
      onRefresh();
    };
    document.addEventListener('visibilitychange', refreshWhenBack);
    return () => document.removeEventListener('visibilitychange', refreshWhenBack);
  }, [onRefresh]);

  const reassignment = useOrderSeatReassignment({
    csrfToken: csrfToken(),
    url: active?.reassignUrl,
    previewUrl: active?.previewUrl,
    previewEmailUrl: active?.previewEmailUrl,
    successMessage: 'Fan moved',
    onReassigned: ({ refundAmount }) => {
      setMoved((current) => ({ ...current, [active.id]: pickedSeat.current }));
      reassignment.close();
      setActive(null);
      onRefresh();
      if (refundAmount) openOrder(active, { refund_ticket_id: active.id, refund_amount: refundAmount });
    },
  });

  if (!report) return null;

  const removed = new Set(report.removedLabels || []);
  const needs = report.sold.filter((row) => !refunded[row.id]).map((row) => ({ ...row, status: 'needs' }));
  const stillSold = new Set(needs.map((row) => row.id));
  const sorted = Object.values(firstSold.current)
    .filter((row) => !stillSold.has(row.id))
    .map((row) => {
      if (moved[row.id]) return { ...row, status: 'moved', to: moved[row.id] };
      return { ...row, status: refunded[row.id] ? 'refunded' : 'sorted' };
    });
  const taken = report.taken || [];
  const freeingUp = report.releasing || [];
  const releasedOnly = releasedHolds.filter((hold) => !report.held.some((h) => h.seat === hold.seat));
  const shrunk = report.shrunk || [];
  const remaining = needs.length + report.held.length + report.carts.length + report.killed.length + taken.length + shrunk.length + freeingUp.length;
  mostInTheWay.current = Math.max(mostInTheWay.current, remaining + sorted.length);
  const total = mostInTheWay.current;
  const done = total - remaining;
  const everyoneSorted = remaining === 0;

  const changeSeat = (row) => {
    setActive(row);
    reassignment.openFor({ id: row.id, seatAssignment: row.seatAssignment, seatingCategoryKey: row.seatingCategoryKey, finalCost: row.paid });
  };

  const submitSeat = (label, ...upgrade) => {
    if (removed.has(label) || shrunk.some((area) => area.seat === label)) {
      toastError(`${label} is being removed too. Pick another seat.`);
      return Promise.resolve();
    }
    pickedSeat.current = label;
    return reassignment.submit(label, ...upgrade);
  };

  const selectedRows = needs.filter((row) => row.movable && selected.includes(row.id));
  const refundRows = report.seatRefundsUrl ? needs.filter((row) => row.refundable && selected.includes(row.id)) : [];

  const refundFans = async ({ reason, sendReceipt }) => {
    setSubmittingRefund(true);
    try {
      await axios.post(report.seatRefundsUrl, { ticket_ids: refunding.map((row) => row.id), reason, send_receipt: sendReceipt }, { headers: { 'X-CSRF-TOKEN': csrfToken() } });
      setRefunded((current) => ({ ...current, ...Object.fromEntries(refunding.map((row) => [row.id, true])) }));
      toastSuccess(`Refunded ${refunding.length} ${refunding.length === 1 ? 'ticket' : 'tickets'}`);
      setRefunding(null);
      setSelected([]);
    } catch (err) {
      const errors = err.response && err.response.data;
      toastError(Array.isArray(errors) && errors.length ? errors.join(' ') : "Couldn't refund those fans. Try again.");
    }
    setSubmittingRefund(false);
    onRefresh();
  };

  const bestAvailable = async () => {
    const rows = selectedRows.length ? selectedRows : needs.filter((row) => row.movable);
    try {
      const { data } = await axios.get(`${report.seatMovesUrl}/best_available`, { params: { ticket_ids: rows.map((row) => row.id), avoid: [...removed, ...shrunk.map((area) => area.seat)] } });
      setSuggested(rows.map((row) => data.picks[row.id] || null));
      setMovingMany(rows);
    } catch (err) {
      toastError("Couldn't find open seats. Try Pick on map.");
    }
  };

  const submitMany = async (moves, email) => {
    const clash = moves.find((move) => removed.has(move.label) || shrunk.some((area) => area.seat === move.label));
    if (clash) {
      toastError(`${clash.label} is being removed too. Pick another seat.`);
      return;
    }
    setSubmittingMany(true);
    try {
      await axios.post(report.seatMovesUrl, {
        moves: moves.map((move) => ({ ticket_id: move.ticketId, to_seatsio_object_id: move.label })),
        email_fan: email.emailFan,
        fan_note: email.fanNote,
      }, { headers: { 'X-CSRF-TOKEN': csrfToken() } });
      setMoved((current) => ({ ...current, ...Object.fromEntries(moves.map((move) => [move.ticketId, move.label])) }));
      toastSuccess(`Moved ${moves.length} ${moves.length === 1 ? 'fan' : 'fans'}`);
      setMovingMany(null);
      setSelected([]);
    } catch (err) {
      const data = (err.response && err.response.data) || {};
      const movedIds = data.moved || [];
      if (movedIds.length) {
        setMoved((current) => ({ ...current, ...Object.fromEntries(moves.filter((move) => movedIds.includes(move.ticketId)).map((move) => [move.ticketId, move.label])) }));
        setMovingMany(null);
        setSelected([]);
      }
      const prefix = movedIds.length ? `Moved ${movedIds.length} of ${moves.length}. ` : '';
      toastError(`${prefix}${data.errors ? [].concat(data.errors).join(' ') : "Couldn't move those fans. Try again."}`);
    }
    setSubmittingMany(false);
    onRefresh();
  };

  const release = async (hold) => {
    setReleasing(hold.seat);
    try {
      const keep = hold.holdObjects.filter((label) => label !== hold.seat && !released.current.has(label));
      const headers = { 'X-CSRF-TOKEN': csrfToken() };
      if (keep.length || hold.holdHasPlaces) {
        await axios.patch(hold.holdUrl, { seating_event_hold: { objects: keep } }, { headers });
      } else {
        await axios.delete(hold.holdUrl, { headers });
      }
      released.current.add(hold.seat);
      setReleasedHolds((current) => [...current, hold]);
      toastSuccess(`Released ${hold.seat} from ${hold.holdName}`);
      onRefresh();
    } catch (err) {
      toastError(`Couldn't release ${hold.seat}. Try again.`);
    }
    setReleasing(null);
  };

  const openOrder = (row, params = null) => {
    orderOpened.current = true;
    const url = params ? `${row.orderUrl}?${new URLSearchParams(params)}` : row.orderUrl;
    const tab = window.open(url, '_blank');
    if (tab) tab.opener = null;
    else if (params) toastError(`Your browser blocked the new tab. Open order #${row.orderId} and use Refund to refund the ${money(params.refund_amount)} difference.`);
  };

  const columns = [
    { key: 'seat', header: 'Seat', width: 64, render: (row) => <strong style={{ color: '#333', fontWeight: 600 }}>{row.seat}</strong> },
    { key: 'fan', header: 'Fan', primary: true, render: (row) => <CellText primary={<span>{row.fan}{row.late ? <span style={{ marginLeft: 6 }}><Badge size="sm" color="blue">Just bought</Badge></span> : null}</span>} supporting={`Order #${row.orderId}`} /> },
    { key: 'ticket', header: 'Ticket', render: (row) => <CellText primary={row.section} supporting={[row.price, money(row.paid)].filter(Boolean).join(' · ')} /> },
    { key: 'status', header: 'Status', render: statusBadge },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) => (
        <Menu
          placement="bottom-end"
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${row.seat}`} />}
          items={[
            ...(row.movable ? [{ label: 'Change seat', onClick: () => changeSeat(row) }] : []),
            { label: 'Refund', destructive: true, onClick: () => (row.refundable && report.seatRefundsUrl ? setRefunding([row]) : openOrder(row)) },
            { divider: true },
            { label: `View order #${row.orderId}`, onClick: () => openOrder(row) },
          ]}
        />
      ),
    },
  ];

  const sectionTitle = (title, count) => (
    <div style={sectionTitleStyle}>{title} <span style={countStyle}>{count}</span></div>
  );
  const miniRow = (key, primary, supporting, trailing) => (
    <div key={key} style={miniRowStyle}>
      <CellText primary={primary} supporting={supporting} />
      {trailing}
    </div>
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        size="xl"
        width={760}
        backdrop
        data-above-sidebar
        title="Seats in the way"
        subtitle={[removedSummary(report.removedLabels || []), report.show?.name, report.show?.date].filter(Boolean).join(' \u00b7 ')}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 12 }}>
            <span style={hint}>{everyoneSorted ? 'Everyone is sorted.' : 'Changes to fans happen right away. The seat map saves when you save it.'}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="secondary" onClick={onRefresh}>Refresh</Button>
              <Button variant="secondary" onClick={onClose}>Close</Button>
              {everyoneSorted ? <Button variant="primary" icon={Save01} onClick={onSave}>{saveLabel}</Button> : null}
            </div>
          </div>
        )}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <ProgressBar value={total ? Math.round((done / total) * 100) : 100} tone={everyoneSorted ? 'success' : 'brand'} label="right" format={() => `${done} of ${total} sorted`} />
          {report.removedOffSale ? <span style={hint}>The removed seats stay off sale while you work, so no one new buys them.</span> : null}
          <section>
            <div style={{ marginBottom: 12 }}>
              {sectionTitle('Needs a seat', needs.length)}
              <div style={hint}>Sold tickets on seats you removed. Move these fans or refund them.</div>
              {needs.some((row) => row.movable) || refundRows.length ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  {needs.some((row) => row.movable) ? <Button size="sm" variant="primary" onClick={bestAvailable}>{selectedRows.length ? `Best available (${selectedRows.length})` : 'Best available (all)'}</Button> : null}
                  {selectedRows.length ? <Button size="sm" variant="secondary" onClick={() => { setSuggested(null); setMovingMany(selectedRows); }}>{`Pick on map (${selectedRows.length})`}</Button> : null}
                  {refundRows.length ? <Button size="sm" variant="secondary" onClick={() => setRefunding(refundRows)}>{`Refund (${refundRows.length})`}</Button> : null}
                </div>
              ) : null}
            </div>
            {needs.length ? (
              <Table columns={columns} data={needs} getRowKey={(row) => row.id} selectable selectedKeys={selected} onSelectionChange={setSelected} stickyLastColumn stackBelow={640} />
            ) : miniRow('done', 'Every sold ticket has a new seat or a refund', 'Nothing left to do here.', <Badge size="sm" color="success">Done</Badge>)}
            {sorted.length ? (
              <div style={{ marginTop: 16 }}>
                <div style={fieldLabelStyle}>Sorted <span style={countStyle}>{sorted.length}</span></div>
                <Table columns={columns.filter((column) => column.key !== 'actions')} data={sorted} getRowKey={(row) => row.id} stackBelow={640} />
              </div>
            ) : null}
          </section>
          {report.held.length || releasedOnly.length ? (
            <section>
              {sectionTitle('Held', report.held.length + releasedOnly.length)}
              {report.held.map((hold) => (hold.holdObjects.includes(hold.seat)
                ? miniRow(hold.seat, `${hold.seat} · ${hold.holdName}`, 'Release it from the hold, then it can come off the map.', <Button size="sm" variant="secondary" disabled={releasing !== null} onClick={() => release(hold)}>Release from hold</Button>)
                : miniRow(hold.seat, `${hold.seat} · ${hold.holdName}`, 'Release these places on the Holds tab, then it can come off the map.', null)))}
              {releasedOnly.map((hold) => miniRow(`released-${hold.seat}`, `${hold.seat} · ${hold.holdName}`, 'Released. Re-hold another seat from the Holds tab if you need it.', <Badge size="sm" color="gray">Released</Badge>))}
            </section>
          ) : null}
          {report.killed.length ? (
            <section>
              {sectionTitle('Killed', report.killed.length)}
              {report.killed.map((seat) => miniRow(seat, seat, 'Take it off the Kills tab, then it can come off the map.', null))}
            </section>
          ) : null}
          {shrunk.length ? (
            <section>
              {sectionTitle('Too small for what sold', shrunk.length)}
              {shrunk.map((area) => miniRow(area.seat, area.seat, `${area.over} more ${area.over === 1 ? 'place is' : 'places are'} taken than the new size allows. Make the area bigger, or move or refund ${area.over} from the show's orders.`, null))}
            </section>
          ) : null}
          {freeingUp.length ? (
            <section>
              {sectionTitle('Freeing up', freeingUp.length)}
              {freeingUp.map((seat) => miniRow(seat, seat, 'Its fan was moved or refunded. The seat frees up within a minute. Press Refresh to check.', <Badge size="sm" color="gray" dot>Freeing up</Badge>))}
            </section>
          ) : null}
          {taken.length ? (
            <section>
              {sectionTitle('Still taken', taken.length)}
              {taken.map((seat) => miniRow(seat, seat, "This seat is taken, but not by a ticket you can move here. Check the show's orders, or put the seat back on the map.", null))}
            </section>
          ) : null}
          {report.carts.length ? (
            <section>
              {sectionTitle("In a fan's cart", report.carts.length)}
              {report.carts.map((seat) => miniRow(seat, seat, "Someone was checking out when you saved. If they buy it, they'll show up under Sold.", <Badge size="sm" color="gray" dot>Waiting on checkout</Badge>))}
            </section>
          ) : null}
        </div>
      </Drawer>
      {movingMany ? <style>{'.popup-modal-overlay { z-index: 1200 !important; }'}</style> : null}
      {movingMany ? (
        <SeatReassignmentModal
          initialPicks={suggested}
          tickets={movingMany.map((row) => ({ id: row.id, seat: row.seat, fan: row.fan, seatingCategoryKey: row.seatingCategoryKey, ticketLabel: [row.section, row.price].filter(Boolean).join(' ') }))}
          seating={report.seating}
          submitting={submittingMany}
          onClose={() => { setMovingMany(null); setSuggested(null); }}
          onSubmitMany={submitMany}
        />
      ) : null}
      {refunding ? <style>{'body [data-sl-modal-overlay]:has([data-seat-refund]) { z-index: 1200 !important; }'}</style> : null}
      {refunding ? (
        <SeatRefundDialog
          rows={refunding}
          reasons={report.refundReasons || []}
          defaultReason={report.refundReason}
          submitting={submittingRefund}
          onClose={() => setRefunding(null)}
          onConfirm={refundFans}
        />
      ) : null}
      {reassignment.ticket ? <style>{'.popup-modal-overlay { z-index: 1200 !important; }'}</style> : null}
      {reassignment.ticket ? (
        <SeatReassignmentModal
          ticket={reassignment.ticket}
          seating={report.seating}
          paymentMethods={report.upgradePaymentMethods}
          currency={report.currency}
          canRefund={Boolean(report.canRefund)}
          submitting={reassignment.submitting}
          onClose={() => { reassignment.close(); setActive(null); }}
          onSubmit={submitSeat}
          onPreview={reassignment.preview}
          onPreviewEmail={reassignment.previewEmail}
        />
      ) : null}
    </>
  );
};

SeatsInTheWayPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  report: PropTypes.shape({
    show: PropTypes.shape({ name: PropTypes.string, date: PropTypes.string }),
    sold: PropTypes.arrayOf(PropTypes.object).isRequired,
    held: PropTypes.arrayOf(PropTypes.object).isRequired,
    killed: PropTypes.arrayOf(PropTypes.string).isRequired,
    carts: PropTypes.arrayOf(PropTypes.string).isRequired,
    taken: PropTypes.arrayOf(PropTypes.string),
    releasing: PropTypes.arrayOf(PropTypes.string),
    shrunk: PropTypes.arrayOf(PropTypes.shape({ seat: PropTypes.string, over: PropTypes.number })),
    removedLabels: PropTypes.arrayOf(PropTypes.string),
    removedOffSale: PropTypes.bool,
    seatMovesUrl: PropTypes.string,
    seating: PropTypes.object,
    upgradePaymentMethods: PropTypes.arrayOf(PropTypes.string),
    currency: PropTypes.string,
    canRefund: PropTypes.bool,
    seatRefundsUrl: PropTypes.string,
    refundReason: PropTypes.string,
    refundReasons: PropTypes.arrayOf(PropTypes.string),
  }),
  onRefresh: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saveLabel: PropTypes.string,
};

SeatsInTheWayPanel.defaultProps = {
  report: null,
  saveLabel: 'Save and Exit',
};

export default SeatsInTheWayPanel;
